import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Slider,
  Divider,
  Chip,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useHistory, useParams, useLocation } from "react-router-dom";
import LabelCanvas from "./components/LabelCanvas";
import LabelPropertiesPanel from "./components/LabelPropertiesPanel";
import { defaultLabel, makeQrElement, makeTextElement, labelsPerPage } from "./labelSchema";
import { generateZpl } from "./render/zplRenderer";
import { overflowsLabel, overlappingPairs } from "./render/labelGeometry";
import { saveLabelTemplate, getLabelTemplateById } from "../../services/LabelTemplate";
import FormAlert from "../../components/FormAlert";

const LIST_PATH = "/template/label-designer";

/** Stand-in values so the canvas shows a realistic sticker while designing. */
const sampleRecord = (product) => ({
  barcode: product.barcode || "CODE",
  productName: product.productName || "Product",
  productionId: "4821",
  billNumber: "BILL-2291",
  packedBy: "—",
  packedDate: new Date().toISOString().slice(0, 10),
  serial: "0001",
  index: 1,
  total: 100,
});

/** What "unsaved changes" compares; excludes transient UI state like selection. */
const snapshot = (title, visibility, label) => JSON.stringify({ title, visibility, label });

export default function LabelDesignerPage() {
  const history = useHistory();
  const location = useLocation();
  const { labelId } = useParams();
  const isEdit = Boolean(labelId);

  // On create, the product arrives from the selection step on the list page.
  const passed = (location.state && location.state.product) || {};

  const [title, setTitle] = useState(
    (location.state && location.state.title) ||
      (passed.productName ? `${passed.productName} label` : "")
  );
  const [product, setProduct] = useState({
    barcode: passed.barcode || "",
    productName: passed.productName || "",
  });
  const [visibility, setVisibility] = useState("SHARED");
  const [label, setLabel] = useState(() => defaultLabel());
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(10);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [showZpl, setShowZpl] = useState(false);

  // Baseline for the unsaved-changes check, reset whenever the document is loaded
  // or successfully saved.
  const baseline = useRef(null);
  if (baseline.current === null) baseline.current = snapshot(title, visibility, label);

  // A create that was not routed through the product picker has no product to design
  // for, so send the user back to pick one rather than saving an orphan label.
  useEffect(() => {
    if (!isEdit && !passed.barcode) {
      history.replace(LIST_PATH);
    }
  }, [isEdit, passed.barcode, history]);

  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      const row = await getLabelTemplateById(labelId);
      if (!alive) return;
      if (row) {
        const loadedLabel = { ...defaultLabel(), ...(row.layout || {}) };
        const loadedVisibility = row.visibility || "SHARED";
        setTitle(row.name || "");
        setProduct({ barcode: row.barcode || "", productName: row.productName || "" });
        setVisibility(loadedVisibility);
        setLabel(loadedLabel);
        baseline.current = snapshot(row.name || "", loadedVisibility, loadedLabel);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [isEdit, labelId]);

  const patch = (p) => setLabel((l) => ({ ...l, ...p }));

  const patchElement = (id, p) =>
    setLabel((l) => ({
      ...l,
      elements: l.elements.map((e) => (e.id === id ? { ...e, ...p } : e)),
    }));

  const addElement = (el) => {
    setLabel((l) => ({ ...l, elements: [...l.elements, el] }));
    setSelectedId(el.id);
  };

  const deleteElement = (id) => {
    setLabel((l) => ({ ...l, elements: l.elements.filter((e) => e.id !== id) }));
    setSelectedId((cur) => (cur === id ? null : cur));
  };

  // Delete / Backspace removes the selection, but never while a field is focused.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (!selectedId) return;
      const t = e.target;
      if (t && (t.isContentEditable || t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      deleteElement(selectedId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId]);

  const sample = useMemo(() => sampleRecord(product), [product]);

  const zpl = useMemo(() => {
    try {
      return generateZpl(label, { records: [sample] });
    } catch (e) {
      return "; " + e.message;
    }
  }, [label, sample]);

  const dirty = snapshot(title, visibility, label) !== baseline.current;
  const clipped = label.elements.filter((e) => overflowsLabel(e, label));
  const collisions = overlappingPairs(label.elements, label.dpmm);

  const leave = () => history.push(LIST_PATH);

  const handleBack = () => {
    if (dirty) setConfirmLeave(true);
    else leave();
  };

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setAlertData({ type: "error", message: "Give the label a title before saving." });
      return;
    }
    setSaving(true);
    setAlertData(null);
    const saved = await saveLabelTemplate({
      id: isEdit ? Number(labelId) : null,
      name: title.trim(),
      barcode: product.barcode,
      productName: product.productName,
      layout: label,
      visibility,
    });
    setSaving(false);

    if (saved) {
      // Clear the dirty flag before navigating so the leave guard does not fire.
      baseline.current = snapshot(title, visibility, label);
      history.push(LIST_PATH);
    } else {
      setAlertData({ type: "error", message: "Could not save the label. Please try again." });
    }
  }, [title, isEdit, labelId, product, label, visibility, history]);

  return (
    <Dialog fullScreen open onClose={handleBack}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} title="Back to labels">
            <ArrowBackIcon />
          </IconButton>
          <QrCode2Icon color="info" sx={{ mr: 1 }} />
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {isEdit ? "Edit Label" : "New Label"}
            {product.productName ? ` — ${product.productName}` : ""}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {dirty && <Chip size="small" color="warning" variant="outlined" label="Unsaved" />}
            {collisions.length > 0 && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${collisions.length} overlap${collisions.length > 1 ? "s" : ""}`}
              />
            )}
            {clipped.length > 0 && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`${clipped.length} off-label`}
              />
            )}
            <Chip size="small" label={`${Math.round(label.dpmm * 25.4)} dpi`} />
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              onClick={handleSave}
            >
              Save Template
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 64px)",
            minHeight: 0,
            p: 2,
            pt: 1.5,
          }}
        >
          {alertData && (
            <Box sx={{ mb: 1.5 }}>
              <FormAlert type={alertData.type} message={alertData.message} />
            </Box>
          )}

          <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Label title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ minWidth: 260 }}
              required
            />
            <TextField
              size="small"
              label="Product"
              value={product.productName}
              sx={{ minWidth: 220 }}
              InputProps={{ readOnly: true }}
              helperText="Chosen when the label was created"
            />
            <TextField
              size="small"
              label="Product code"
              value={product.barcode}
              sx={{ minWidth: 160 }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              select
              size="small"
              label="Visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="SHARED">Shared</MenuItem>
              <MenuItem value="PRIVATE">Private</MenuItem>
            </TextField>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Zoom
                </Typography>
                <Slider
                  size="small"
                  min={2}
                  max={24}
                  step={1}
                  value={zoom}
                  onChange={(e, v) => setZoom(v)}
                  sx={{ maxWidth: 180 }}
                  valueLabelDisplay="auto"
                />
                <Divider orientation="vertical" flexItem />
                <Typography variant="caption" color="text.secondary">
                  Drag to move · drag a handle to resize · Delete removes the selected element
                </Typography>
              </Stack>
              <Divider />
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  bgcolor: "grey.300",
                  overflow: "auto",
                }}
              >
                <LabelCanvas
                  label={label}
                  pxPerMm={zoom}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onChange={patchElement}
                  sample={sample}
                />
              </Box>
              <Divider />
              <Box sx={{ px: 1, py: 0.5, flexShrink: 0 }}>
                <Button
                  size="small"
                  onClick={() => setShowZpl((v) => !v)}
                  endIcon={showZpl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  Generated ZPL · {labelsPerPage(label)} per page
                </Button>
                <Collapse in={showZpl}>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      p: 1,
                      bgcolor: "grey.900",
                      color: "grey.100",
                      borderRadius: 1,
                      fontSize: 11,
                      lineHeight: 1.5,
                      maxHeight: 200,
                      overflow: "auto",
                    }}
                  >
                    {zpl}
                  </Box>
                </Collapse>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ width: 350, flexShrink: 0, minHeight: 0 }}>
              <LabelPropertiesPanel
                label={label}
                patch={patch}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onAddQr={() => addElement(makeQrElement())}
                onAddText={() => addElement(makeTextElement())}
                onDeleteElement={deleteElement}
                onPatchElement={patchElement}
              />
            </Paper>
          </Box>
        </Box>
      )}

      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Leave without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This label has changes that have not been saved. Leaving now discards them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>Keep editing</Button>
          <Button
            onClick={() => {
              setConfirmLeave(false);
              handleSave();
            }}
          >
            Save &amp; leave
          </Button>
          <Button color="error" variant="contained" onClick={leave}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
